import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Plus, ArrowLeft, Save, Trash2, Send, Eye, Search,
  FileText, Image as ImageIcon, X, Sparkles, MessageCircle,
} from "lucide-react";
import WhatsAppTemplates from "./WhatsAppTemplates";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_TPL = {
  name: "",
  subject: "",
  body: "",
  footer_note: "",
  logo_url: "",
  attach: {},
  suggested_trigger: "",
};

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("ro-RO"); } catch { return d; }
}

export default function EmailTemplates() {
  const [tab, setTab] = useState("email"); // "email" | "whatsapp"
  const [templates, setTemplates] = useState([]);
  const [catalog, setCatalog] = useState({ variables: {}, attachments: [], triggers: [] });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = list view, object = editor
  const [search, setSearch] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, catRes] = await Promise.all([
        axios.get(`${API}/email-templates`),
        axios.get(`${API}/email-templates/catalog`),
      ]);
      setTemplates(tplRes.data?.items || []);
      setCatalog(catRes.data || { variables: {}, attachments: [], triggers: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleDelete = async (id) => {
    if (!window.confirm("Sigur ștergi acest șablon?")) return;
    try {
      await axios.delete(`${API}/email-templates/${id}`);
      toast.success("Șablon șters");
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const filtered = useMemo(
    () => templates.filter(t => !search.trim() || t.name.toLowerCase().includes(search.toLowerCase())),
    [templates, search]
  );

  const TabsHeader = (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 p-2 shadow-lg shadow-emerald-500/20">
          <Mail className="h-4 w-4 text-white" />
        </div>
        <div>
          <h1 className="text-base font-semibold text-zinc-50">Șabloane</h1>
          <p className="text-[11px] text-zinc-500">Definește mesajele trimise automat clienților din fluxurile de Automatizări</p>
        </div>
      </div>
      <div className="inline-flex rounded-md border border-zinc-800 bg-zinc-900 p-0.5" data-testid="templates-tabs">
        <button
          data-testid="tab-email"
          onClick={() => setTab("email")}
          className={`inline-flex items-center gap-1.5 rounded-[5px] px-3 py-1.5 text-xs font-medium transition ${
            tab === "email" ? "bg-emerald-500/20 text-emerald-100" : "text-zinc-400 hover:text-zinc-100"
          }`}
        >
          <Mail className="h-3.5 w-3.5" /> Email
        </button>
        <button
          data-testid="tab-whatsapp"
          onClick={() => setTab("whatsapp")}
          className={`inline-flex items-center gap-1.5 rounded-[5px] px-3 py-1.5 text-xs font-medium transition ${
            tab === "whatsapp" ? "bg-emerald-500/20 text-emerald-100" : "text-zinc-400 hover:text-zinc-100"
          }`}
        >
          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
        </button>
      </div>
    </div>
  );

  if (tab === "whatsapp") {
    return (
      <div data-testid="templates-page" className="min-h-[calc(100vh-3.5rem)] bg-zinc-950 px-4 py-5 text-zinc-100 sm:px-8">
        {TabsHeader}
        <WhatsAppTemplates />
      </div>
    );
  }

  if (editing) {
    return (
      <EditorView
        initial={editing}
        catalog={catalog}
        onCancel={() => setEditing(null)}
        onSaved={async () => { await loadAll(); setEditing(null); }}
      />
    );
  }

  return (
    <div data-testid="email-templates-page" className="min-h-[calc(100vh-3.5rem)] bg-zinc-950 px-4 py-5 text-zinc-100 sm:px-8">
      {TabsHeader}
      <div className="mb-5 flex items-center justify-end">
        <button
          data-testid="tpl-new-btn"
          onClick={() => setEditing({ ...EMPTY_TPL })}
          className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/15 px-3 py-1.5 text-xs font-medium text-emerald-100 hover:bg-emerald-500/25"
        >
          <Plus className="h-3.5 w-3.5" /> Adaugă șablon
        </button>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="tpl-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută după nume…"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 pl-7 pr-3 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-emerald-500/50 focus:outline-none"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/40">
        <table className="w-full text-xs">
          <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-4 py-3 text-left">Nume</th>
              <th className="px-4 py-3 text-left">Subject</th>
              <th className="px-4 py-3 text-left">Trigger sugerat</th>
              <th className="px-4 py-3 text-left">Atașamente</th>
              <th className="px-4 py-3 text-left">Actualizat</th>
              <th className="px-4 py-3 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-zinc-500">Se încarcă…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center">
                <Mail className="mx-auto h-8 w-8 text-zinc-700" />
                <p className="mt-2 text-zinc-500">Niciun șablon definit încă.</p>
                <p className="mt-1 text-[10px] text-zinc-600">Apasă „Adaugă șablon&rdquo; pentru a începe.</p>
              </td></tr>
            )}
            {filtered.map((t) => {
              const attachCount = Object.values(t.attach || {}).filter(Boolean).length;
              const triggerLabel = catalog.triggers.find(x => x.key === t.suggested_trigger)?.label;
              return (
                <tr key={t.id} data-testid={`tpl-row-${t.id}`} className="hover:bg-zinc-900/40">
                  <td className="px-4 py-2.5 font-medium text-zinc-100">{t.name}</td>
                  <td className="px-4 py-2.5 text-zinc-400">{t.subject || "—"}</td>
                  <td className="px-4 py-2.5">
                    {triggerLabel ? (
                      <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] text-violet-200 ring-1 ring-violet-500/30">{triggerLabel}</span>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-zinc-400">{attachCount > 0 ? `${attachCount} ataș.` : "—"}</td>
                  <td className="px-4 py-2.5 text-zinc-500">{fmt(t.updated_at || t.created_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        data-testid={`tpl-edit-${t.id}`}
                        onClick={() => setEditing(t)}
                        className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-200"
                      >Editează</button>
                      <button
                        data-testid={`tpl-delete-${t.id}`}
                        onClick={() => handleDelete(t.id)}
                        className="rounded-md border border-zinc-800 bg-zinc-900 p-1 text-zinc-400 hover:border-rose-500/40 hover:text-rose-300"
                      ><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditorView({ initial, catalog, onCancel, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY_TPL, ...initial });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewOrderId, setPreviewOrderId] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);
  const [activeField, setActiveField] = useState("body"); // body | subject | footer

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const refFor = (field) => ({ subject: subjectRef, body: bodyRef, footer: footerRef })[field];

  const insertVariable = (key) => {
    const field = activeField;
    const ref = refFor(field);
    const inp = ref?.current;
    const insertion = `\${${key}}`;
    const fieldName = field === "body" ? "body" : field === "subject" ? "subject" : "footer_note";
    if (!inp) {
      update({ [fieldName]: (form[fieldName] || "") + insertion });
      return;
    }
    const start = inp.selectionStart ?? (form[fieldName] || "").length;
    const end = inp.selectionEnd ?? (form[fieldName] || "").length;
    const next = (form[fieldName] || "").slice(0, start) + insertion + (form[fieldName] || "").slice(end);
    update({ [fieldName]: next });
    setTimeout(() => {
      inp.focus();
      const pos = start + insertion.length;
      inp.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Numele șablonului este obligatoriu"); return; }
    setSaving(true);
    try {
      let saved;
      if (form.id) {
        const r = await axios.put(`${API}/email-templates/${form.id}`, stripId(form));
        saved = r.data;
      } else {
        const r = await axios.post(`${API}/email-templates`, stripId(form));
        saved = r.data;
      }
      toast.success("Salvat");
      onSaved(saved);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const handlePreview = async () => {
    if (!form.id) { toast.error("Salvează șablonul mai întâi"); return; }
    try {
      const r = await axios.post(`${API}/email-templates/${form.id}/render`, { order_id: previewOrderId || undefined });
      setPreviewHtml(r.data?.html || "");
      setPreviewSubject(r.data?.subject || "");
      setShowPreview(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const handleTestSend = async () => {
    if (!form.id) { toast.error("Salvează mai întâi"); return; }
    if (!testEmail.trim()) { toast.error("Introdu un email de test"); return; }
    setSendingTest(true);
    try {
      await axios.post(`${API}/email-templates/${form.id}/test`, { to: testEmail.trim(), order_id: previewOrderId || undefined });
      toast.success(`Email trimis la ${testEmail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSendingTest(false); }
  };

  return (
    <div data-testid="email-template-editor" className="min-h-[calc(100vh-3.5rem)] bg-zinc-950 px-4 py-5 text-zinc-100 sm:px-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button data-testid="editor-back-btn" onClick={onCancel} className="rounded-md border border-zinc-800 bg-zinc-900 p-2 hover:border-zinc-700"><ArrowLeft className="h-4 w-4" /></button>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Șabloane email</p>
            <h1 className="text-base font-semibold text-zinc-50">{form.id ? "Editează șablon" : "Șablon nou"}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          {form.id && (
            <button data-testid="editor-preview-btn" onClick={handlePreview} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-sky-500/40 hover:text-sky-200">
              <Eye className="h-3.5 w-3.5" /> Preview
            </button>
          )}
          <button data-testid="editor-save-btn" onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/20 px-3 py-1.5 text-xs font-medium text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-40">
            <Save className="h-3.5 w-3.5" /> {saving ? "Se salvează…" : "Salvare"}
          </button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* LEFT — variables catalog */}
        <aside className="space-y-4 lg:col-span-1">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
              <Sparkles className="h-3 w-3 text-violet-300" /> Variabile · click pentru inserare
            </div>
            <div className="mb-3 rounded-md bg-zinc-900 px-2.5 py-2 text-[10px] text-zinc-400">
              Câmp activ: <span className="font-medium text-zinc-200">{activeField === "subject" ? "Subiect" : activeField === "footer" ? "Notă subsol" : "Conținut email"}</span>
            </div>
            <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
              {Object.entries(catalog.variables || {}).map(([group, vars]) => (
                <div key={group}>
                  <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-zinc-500">{group}</p>
                  <div className="flex flex-wrap gap-1">
                    {vars.map((v) => (
                      <button
                        key={v.key}
                        data-testid={`var-${v.key}`}
                        onClick={() => insertVariable(v.key)}
                        className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-300 transition hover:border-emerald-500/40 hover:text-emerald-200"
                        title={`\${${v.key}}`}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* RIGHT — form */}
        <section className="space-y-4 lg:col-span-2">
          <Field label="Nume template">
            <input
              data-testid="field-name"
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="ex: Înregistrare comandă"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-emerald-500/50 focus:outline-none"
            />
          </Field>

          <Field label="Trigger sugerat (folosit în Automatizări)">
            <select
              data-testid="field-trigger"
              value={form.suggested_trigger || ""}
              onChange={(e) => update({ suggested_trigger: e.target.value })}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500/50 focus:outline-none"
            >
              <option value="" className="bg-zinc-900">— niciunul —</option>
              {(catalog.triggers || []).map((t) => (
                <option key={t.key} value={t.key} className="bg-zinc-900">{t.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Subiectul emailului">
            <input
              ref={subjectRef}
              data-testid="field-subject"
              value={form.subject}
              onChange={(e) => update({ subject: e.target.value })}
              onFocus={() => setActiveField("subject")}
              placeholder="ex: Confirmare înregistrare comandă ${order.id}"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-emerald-500/50 focus:outline-none"
            />
          </Field>

          <Field label="Adauga sablon (conținutul emailului)">
            <textarea
              ref={bodyRef}
              data-testid="field-body"
              value={form.body}
              onChange={(e) => update({ body: e.target.value })}
              onFocus={() => setActiveField("body")}
              rows={10}
              placeholder={"Salut ${client.name},\n\nProduse:\n${order.product_name}\n\nDetalii comandă: ${order.id}"}
              className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-emerald-500/50 focus:outline-none font-mono"
            />
            <p className="mt-1 text-[10px] text-zinc-500">HTML acceptat. Folosește <code className="text-emerald-300">${"${variabila}"}</code> pentru a insera valori dinamice.</p>
          </Field>

          <Field label="Logo">
            <LogoUploader value={form.logo_url} onChange={(url) => update({ logo_url: url })} />
          </Field>

          <Field label="Notă de subsol">
            <textarea
              ref={footerRef}
              data-testid="field-footer"
              value={form.footer_note}
              onChange={(e) => update({ footer_note: e.target.value })}
              onFocus={() => setActiveField("footer")}
              rows={3}
              placeholder="Dacă ai nevoie de orice altă informație vizavi de statusul comenzii tale ne poți contacta de luni pana vineri intre orele 09:00-16:00 la numărul de telefon 0750433328"
              className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-emerald-500/50 focus:outline-none"
            />
          </Field>

          <Field label="Atașamente">
            <div className="flex flex-wrap gap-2">
              {(catalog.attachments || []).map((a) => (
                <label key={a.key} data-testid={`attach-${a.key}`} className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-[11px] text-zinc-300 transition hover:border-emerald-500/40 has-[:checked]:border-emerald-500/60 has-[:checked]:bg-emerald-500/10 has-[:checked]:text-emerald-100">
                  <input
                    type="checkbox"
                    checked={!!form.attach?.[a.key]}
                    onChange={(e) => update({ attach: { ...form.attach, [a.key]: e.target.checked } })}
                    className="h-3 w-3 accent-emerald-500"
                  />
                  {a.label}
                </label>
              ))}
            </div>
            <p className="mt-1 text-[10px] text-zinc-500">Atașamentele se preiau automat din comanda specifică (Factură PDF din Oblio, AWB din eMAG/Sameday) la momentul trimiterii reale.</p>
          </Field>

          {form.id && (
            <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-3">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-sky-300">Test trimitere</p>
              <div className="grid gap-2 sm:grid-cols-[1fr,140px,auto]">
                <input
                  data-testid="test-email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="email@destinatie.ro"
                  className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-sky-500/50 focus:outline-none"
                />
                <input
                  data-testid="test-order-id"
                  value={previewOrderId}
                  onChange={(e) => setPreviewOrderId(e.target.value)}
                  placeholder="ID comandă (opțional)"
                  className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-sky-500/50 focus:outline-none"
                />
                <button
                  data-testid="test-send-btn"
                  onClick={handleTestSend}
                  disabled={sendingTest}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md border border-sky-500/50 bg-sky-500/20 px-3 py-2 text-xs font-medium text-sky-100 hover:bg-sky-500/30 disabled:opacity-40"
                >
                  <Send className="h-3.5 w-3.5" /> {sendingTest ? "Se trimite…" : "Trimite test"}
                </button>
              </div>
              <p className="mt-1.5 text-[10px] text-sky-300/70">Dacă lași ID comandă gol, se folosesc date demo. Dacă completezi, atașamentele reale (Factură, AWB) vor fi incluse.</p>
            </div>
          )}
        </section>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="preview-modal">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl border border-zinc-800 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-gray-500">Preview email</p>
                <p className="truncate text-sm font-medium text-gray-900">{previewSubject}</p>
              </div>
              <button onClick={() => setShowPreview(false)} className="rounded-md p-1 text-gray-500 hover:bg-gray-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto bg-gray-50">
              <div className="bg-white" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-zinc-400">{label}</label>
      {children}
    </div>
  );
}

function LogoUploader({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data?.url || "");
      toast.success("Logo încărcat");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setUploading(false); }
  };

  if (value) {
    return (
      <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-4 text-center">
        <img src={value} alt="logo" className="mx-auto max-h-20" />
        <button data-testid="logo-remove-btn" onClick={() => onChange("")} className="mt-2 inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-[10px] text-rose-200 hover:bg-rose-500/20">
          <Trash2 className="h-3 w-3" /> Șterge
        </button>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="flex-1 truncate rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-left text-sm text-zinc-400 hover:border-zinc-700"
      >Alege un fișier</button>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
      <button data-testid="logo-upload-btn" onClick={() => inputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-200 disabled:opacity-40">
        <ImageIcon className="h-3.5 w-3.5" /> {uploading ? "Se încarcă…" : "Încarcă"}
      </button>
    </div>
  );
}

function stripId(t) {
  const { id, _id, created_at, updated_at, ...rest } = t;
  return rest;
}
